import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from "@angular/core";
import { ControlContainer, NgForm } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { Tools } from "@comunicare/comunicare-helper";
import {
  Careplan,
  Entity,
  FORMS_MODE,
  IActivity,
  ICareplan,
  ICycleSchema,
  IOrder,
  IReference,
  IStepwiseDrug,
  KeyValue,
} from "@comunicare/comunicare-interfaces";
import { TimingEditorComponent } from "@comunicare/comunicare-timing-editor";
import * as moment from "moment";
import { DrugCommentComponent } from "../../patient-drugs/drug-add/drug-comment/drug-comment.component";

@Component({
  selector: "app-careplan-activity",
  templateUrl: "./careplan-activity.component.html",
  styleUrls: ["./careplan-activity.component.scss"],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class CareplanActivityComponent implements OnInit {
  @Input() activity: IActivity;
  @Input() activityIndex: number;
  @Input() careplan: ICareplan;
  @Input() noEndDate: boolean;
  @Input() authorizedToEdit: boolean;
  @Input() createNewCareplan: boolean;
  @Input() protocols: { [id: string]: Array<IReference> };
  @Input() routes: { [id: string]: Array<IReference> };
  @Input() administrations: { [id: string]: Array<IReference> };
  @Input() diluents: { [id: string]: Array<IReference> };
  @Input() administrators: { [id: string]: Array<IReference> };
  @Input() drugCycles: { [id: string]: ICycleSchema };
  @Input() drugStepwises: { [id: string]: IStepwiseDrug };
  @Input() timingCodes: string[];
  @Input() quantities: string[];
  @Input() availablePeriodUnits: KeyValue[];

  @Output() activityChecked = new EventEmitter<void>();
  @Output() startDateChanged = new EventEmitter<void>();
  @Output() endDateChanged = new EventEmitter<void>();
  @Output() periodChanged = new EventEmitter<{ activityIndex: number; actionIndex: number }>();
  @Output() communicationShown = new EventEmitter<IOrder>();
  @Output() useForCareplanEndDate = new EventEmitter<string>();
  @Output() drugCycleChanged = new EventEmitter<ICycleSchema>();
  @Output() stepwiseDrugChanged = new EventEmitter<IStepwiseDrug>();

  @ViewChildren(TimingEditorComponent) drugsTimings: QueryList<TimingEditorComponent>;
  @ViewChildren(DrugCommentComponent) drugCommentComponent: QueryList<DrugCommentComponent>;

  private readonly TIMING_CODE_MAP: Record<string, string> = {
    rise: Entity.TIMING_RISING,
    morning: Entity.TIMING_MORNING,
    noon: Entity.TIMING_NOON,
    evening: Entity.TIMING_EVENING,
    bedtime: Entity.TIMING_BED,
  };

  // UI state for the diluent multi-select. The template binds to this array instead of
  // directly to activity.detail.diluentReference, because the mat-select is in multiple mode
  // and IActivity only stores a single IReference. onDiluentChanged() and updateDiluentDisplay()
  // are responsible for keeping activity.detail.diluentReference in sync.
  public selectedDiluentRefs: IReference[] = [];
  // Quantity entered by the user when exactly one diluent is selected (e.g. "50 mL").
  // Ignored and cleared when multiple diluents are selected.
  public diluentQuantity = "";

  constructor(private cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    // Pre-populate the multi-select (and quantity field) from the saved diluentReference.
    // Note: in the DB, diluentReference.reference and diluentReference.display hold the same
    // human-readable string, so we always match against .display.
    // Three cases to handle:
    //   1. "name1 + name2"  → multiple diluents, split on " + " and match each part
    //   2. "qty name"       → single diluent with a quantity prefix, extract both
    //   3. "name"           → single diluent, exact match
    const activityRef = this.activity.reference?.reference;
    if (!activityRef || !this.diluents?.[activityRef] || !this.activity.detail?.diluentReference?.display) return;

    const savedDisplay = this.activity.detail.diluentReference.display;
    const list = this.diluents[activityRef];

    // Case 1: multiple diluents joined with " + "
    if (savedDisplay.includes(" + ")) {
      const parts = savedDisplay.split(" + ").map((p) => p.trim());
      const matches = parts.map((part) => list.find((d) => d.display === part)).filter((d): d is IReference => !!d);
      if (matches.length === parts.length) {
        this.selectedDiluentRefs = matches;
        return;
      }
    }

    // Case 2: "qty name" — the diluent display is a suffix separated by a space
    const matchWithQty = list.find((d) => savedDisplay.endsWith(` ${d.display}`) && savedDisplay !== d.display);
    if (matchWithQty) {
      this.diluentQuantity = savedDisplay.slice(0, savedDisplay.length - matchWithQty.display.length - 1).trim();
      this.selectedDiluentRefs = [matchWithQty];
      return;
    }

    // Case 3: exact match
    const exactMatch = list.find((d) => d.display === savedDisplay);
    if (exactMatch) this.selectedDiluentRefs = [exactMatch];
  }

  public save(): boolean {
    let valid = true;
    this.drugsTimings.forEach((t) => {
      if (!t.save()) valid = false;
    });
    this.drugCommentComponent.forEach((d) => d.save());
    return valid;
  }

  public isActivityADrug(): boolean {
    return this.activity.detail.category.coding.some((code) => code.code === "drug");
  }

  public isNewActivityDrug(): FORMS_MODE {
    if (this.createNewCareplan) return FORMS_MODE.CREATE;
    if (this.activity.detail.scheduledTiming?.repeat.boundsPeriod.start) return FORMS_MODE.UPDATE;
    return FORMS_MODE.CREATE;
  }

  public onCheckedActivity(): void {
    this.activity.detail.status = this.activity.detail.status === "active" ? "inactive" : "active";
    this.activityChecked.emit();
  }

  public onEndlessActivity(): void {
    if (this.activity.detail.scheduledTiming.repeat.endless) {
      this.activity.detail.scheduledTiming.repeat.boundsPeriod.end = null;
    }
  }

  public onProtocolChanged(): void {
    Careplan.setProtocol(this.activity, this.activity.detail.productReference);
  }

  public onRouteChanged(): void {
    Careplan.setRoute(this.activity, this.activity.detail.routeReference);
  }

  public onAdministrationChanged(): void {
    Careplan.setAdministration(this.activity, this.activity.detail.administrationReference);
  }

  /**
   * Called whenever the diluent multi-select changes.
   * activity.detail.diluentReference is the single IReference persisted by the backend:
   *   - .reference holds the id of the first (or only) selected diluent
   *   - .display is the human-readable string that ends up in the careplan document and is
   *     shown in read-only views; its format depends on the selection:
   *       · 0 selected  → all fields cleared
   *       · 1 selected  → updateDiluentDisplay() composes "<quantity> <name>" or just "<name>"
   *       · 2+ selected → no quantity, display = "name1 + name2 + …"
   */
  public onDiluentChanged(): void {
    const ref = this.activity.detail.diluentReference;
    if (!ref) return;
    if (this.selectedDiluentRefs.length === 0) {
      ref.reference = null;
      ref.display = null;
      ref.type = null;
      this.diluentQuantity = "";
    } else if (this.selectedDiluentRefs.length === 1) {
      const diluent = this.selectedDiluentRefs[0];
      ref.type = diluent.type ?? null;
      // reference and display are kept in sync by updateDiluentDisplay()
      this.updateDiluentDisplay();
    } else {
      // Multiple diluents: quantity is not applicable, clear it and join names with " + ".
      // reference and display hold the same composed string (DB invariant: reference === display).
      this.diluentQuantity = "";
      ref.type = this.selectedDiluentRefs[0].type ?? null;
      const display = this.selectedDiluentRefs.map((d) => d.display).join(" + ");
      ref.display = display;
      ref.reference = display;
    }
  }

  // Called on every keystroke in the quantity input; recomposes the display string.
  public onDiluentQuantityChanged(): void {
    this.updateDiluentDisplay();
  }

  // Composes diluentReference.display as "<quantity> <diluent name>" when a quantity is provided,
  // or just "<diluent name>" otherwise. Only relevant when exactly one diluent is selected.
  // Also keeps .reference in sync with .display (DB invariant: both fields hold the same string).
  private updateDiluentDisplay(): void {
    if (this.selectedDiluentRefs.length !== 1) return;
    const diluent = this.selectedDiluentRefs[0];
    const qty = this.diluentQuantity?.trim();
    const display = qty ? `${qty} ${diluent.display}` : diluent.display;
    this.activity.detail.diluentReference.display = display;
    this.activity.detail.diluentReference.reference = display;
  }

  public onCheckedTiming($event: MatCheckboxChange, timing: string): void {
    const repeat = this.activity.detail.scheduledTiming.repeat;
    let timingCode = repeat.timingCode === "undefined" ? undefined : repeat.timingCode;
    if (repeat.periodUnits !== "h") {
      const entityCode = this.TIMING_CODE_MAP[timing];
      if (entityCode) {
        if ($event.checked) {
          timingCode = (timingCode ?? "") + entityCode;
        } else {
          timingCode = timingCode?.replace(entityCode, "");
          repeat.quantities[timing] = null;
        }
      }
      repeat.timingCode = timingCode;
    }
  }

  public isTimingChecked(timingCodes: string, timing: string): boolean {
    if (!timingCodes) return false;
    const entityCode = this.TIMING_CODE_MAP[timing];
    return entityCode ? timingCodes.includes(entityCode) : false;
  }

  public isActivityEndDateAfterCareplanEnd(): boolean {
    if (this.noEndDate || !this.careplan?.period?.end) return false;
    const careplanEndDate = moment(this.careplan.period.end);
    if (!careplanEndDate.isValid()) return false;
    if (this.activity.detail.scheduledTiming && !this.activity.detail.scheduledTiming.repeat.endless) {
      const activityEndDate = moment(this.activity.detail.scheduledTiming.repeat.boundsPeriod.end);
      return activityEndDate.isValid() && activityEndDate.isAfter(careplanEndDate, "day");
    } else if (this.activity.detail.scheduledPeriod) {
      const activityEndDate = moment(this.activity.detail.scheduledPeriod.end);
      return activityEndDate.isValid() && activityEndDate.isAfter(careplanEndDate, "day");
    }
    return false;
  }

  public useActivityEndDateForCareplan(): void {
    if (this.activity.detail.scheduledTiming) {
      this.useForCareplanEndDate.emit(this.activity.detail.scheduledTiming.repeat.boundsPeriod.end);
    } else if (this.activity.detail.scheduledPeriod) {
      this.useForCareplanEndDate.emit(this.activity.detail.scheduledPeriod.end);
    }
  }

  public displayActionResulting(actions: IOrder[]): boolean {
    if (!actions?.length) return false;
    return !actions.map((a) => a.notDisplay).reduce((a1, a2) => a1 && a2);
  }

  public compareReference(o1: IReference, o2: IReference): boolean {
    return o1?.reference === o2?.reference;
  }

  public compareDrugDisplay(o1: IReference, o2: IReference): boolean {
    return o1?.display === o2?.display;
  }

  public comparePeriodUnitsFn(o1: never, o2: never): boolean {
    return Tools.standardizePeriodUnit(o1) === Tools.standardizePeriodUnit(o2);
  }

  public periodChange(actionIndex: number): void {
    this.periodChanged.emit({ activityIndex: this.activityIndex, actionIndex });
  }

  public showCommunication(action: IOrder): void {
    this.communicationShown.emit(action);
  }

  public detectChanges(): void {
    this.cdr.detectChanges();
  }
}
